import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { SHOWCASE, SAMPLE_NOTE } from "../data/content";
import { usePageMeta } from "../hooks/use-page-meta";
import { track } from "../lib/tracking";

export default function Portfolio() {
  usePageMeta(
    "Portfolio | Celebrity Bouncers — Event Security Showcase",
    "A sample showcase of the event environments Celebrity Bouncers supports across Andhra Pradesh and Telangana."
  );
  const categories = useMemo(() => ["All", ...new Set(SHOWCASE.map((s) => s.category))], []);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const list = filter === "All" ? SHOWCASE : SHOWCASE.filter((s) => s.category === filter);

  return (
    <>
      <PageHero
        testid="portfolio-page-hero"
        eyebrow="Portfolio"
        title="The Environments We Call Work"
        description="A sample showcase of the occasions we are built for. Client events are published only with written permission — privacy is part of the service."
      />
      <section className="container-x py-16 sm:py-24" data-testid="portfolio-page-grid">
        <div className="flex flex-wrap gap-3" role="tablist" aria-label="Filter by category">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`portfolio-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`border px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] transition-all duration-300 ${
                filter === c
                  ? "border-gold bg-gold text-obsidian"
                  : "border-white/15 text-steel hover:border-gold/50 hover:text-gold"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((item, i) => (
            <Reveal key={item.id} delay={(i % 3) * 0.07}>
              <button
                data-testid={`portfolio-card-${item.id}`}
                onClick={() => {
                  setActive(item);
                  track("portfolio_view", { item: item.id, placement: "portfolio" });
                }}
                className="group relative block h-80 w-full overflow-hidden text-left"
              >
                <img
                  src={item.img}
                  alt={item.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1.8s] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/95 via-obsidian/25 to-transparent" />
                <span className="absolute left-4 top-4 border border-gold/40 bg-obsidian/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold backdrop-blur-sm">
                  Sample
                </span>
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-gold">{item.category}</p>
                  <h2 className="mt-2 font-serif text-xl font-medium text-ivory sm:text-2xl">{item.title}</h2>
                  <p className="mt-1 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-steel">
                    <MapPin size={12} className="text-gold" /> {item.city}
                  </p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10">
          <p className="max-w-2xl text-xs italic leading-relaxed text-steel/60">{SAMPLE_NOTE}</p>
        </Reveal>
      </section>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent
          data-testid="portfolio-detail-modal"
          className="max-h-[88vh] overflow-y-auto border-gold/25 bg-obsidian text-ivory sm:max-w-2xl"
        >
          {active && (
            <>
              <div className="relative -mx-6 -mt-6 mb-6 h-64 overflow-hidden sm:h-80">
                <img src={active.img} alt={active.alt} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian to-transparent" />
              </div>
              <DialogHeader>
                <p className="eyebrow mb-2">{active.category} · Sample Showcase</p>
                <DialogTitle className="font-serif text-2xl font-medium text-ivory sm:text-3xl">
                  {active.title}
                </DialogTitle>
              </DialogHeader>
              <p className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-steel">
                <MapPin size={12} className="text-gold" /> {active.city}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-steel sm:text-base">{active.desc}</p>
              <h4 className="mt-6 text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">Services Provided</h4>
              <div className="mt-3 flex flex-wrap gap-2">
                {active.services.map((s) => (
                  <span key={s} className="border border-gold/25 px-3 py-1.5 text-xs text-ivory/80">{s}</span>
                ))}
              </div>
              <p className="mt-6 border-t border-white/10 pt-4 text-[11px] italic leading-relaxed text-steel/60">
                {SAMPLE_NOTE}
              </p>
              <Link
                to="/contact"
                data-testid="portfolio-modal-enquire-btn"
                onClick={() => setActive(null)}
                className="mt-4 inline-block bg-gold px-6 py-3 text-[12px] font-bold uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-gold-soft"
              >
                Plan a Similar Event
              </Link>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
